import { useEffect, useRef, useState } from "react";
import * as Icon from "@phosphor-icons/react/dist/ssr";
import { useModalProvider } from "../../stores/useModalProvider";
import { useCartStore } from "../../stores/useCart";
import { IProductCart, IProductLightbox, InertiaProps } from "@/types/app";
import { usePage } from "@inertiajs/react";
import {
    PattyAlert,
    capturarError,
    delay,
    separadorMiles,
} from "@/utils/global";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import CakeLoader from "../loaders/cakeLoader";
import axios from "axios";

const CartPrev = () => {
    const { relacionados, comunas } = usePage<InertiaProps>().props;
    const { isModalOpen, closeModal } = useModalProvider();
    const {
        productos,
        eliminarProducto,
        total: totalCart,
        vaciarCarrito: cleanCart,
        actualizarCantidad,
    } = useCartStore();

    const vaciarCarrito = async () => {
        try {
            setIsLoading(true);
            await delay(2000);
            const response = await axios.get(
                `${window.origin}/api/cart/destroy`
            );
            if (response.status === 200) {
                setIsLoading(false);
                await PattyAlert({
                    icon: "success",
                    title: "Carrito",
                    message: "Carrito vaciado correctamente",
                });
                cleanCart();
            }
        } catch (err) {
            console.log({ err });
            setIsLoading(false);
            capturarError(err);
        }
    };
    const [showProduct, setShowProduct] = useState<boolean>(false);
    const [productImage, setProductImage] = useState<IProductLightbox[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const captionsRef = useRef(null);
    const [activeTab, setActiveTab] = useState<string | undefined>("");
    const [comunaSeleccionada, setComunaSeleccionada] = useState<number>(-1);

    const calcularEnvio = async () => {
        if (comunaSeleccionada === -1) {
            await PattyAlert({
                icon: "error",
                title: "Envio",
                message: "Debes seleccionar una comuna",
            });
            return;
        }

        const comuna = [...comunas].find(
            (comuna) => comuna.id === comunaSeleccionada
        );

        if (comuna) {
            await PattyAlert({
                icon: "success",
                title: "Envio",
                message: `El envio a ${comuna.nombre} tiene un costo de $${separadorMiles(
                    comuna.valor ?? 0
                )}`,
            });
        }
    };

    const toggleProductImage = (image: IProductLightbox) => {
        setProductImage([image]);
        setShowProduct(true);
    };

    const eliminarProductoModal = async (producto: IProductCart) => {
        if (!producto || !producto.row_id) {
            return PattyAlert({
                icon: "error",
                title: "Producto",
                message: "Producto no encontrado",
            });
        }

        try {
            setIsLoading(true);
            await delay(2000);
            const url = `${window.origin}/api/cart/delete`;
            const response = await axios.post(url, {
                rowId: producto.row_id,
            });
            if (response.status === 200) {
                setIsLoading(false);
                eliminarProducto(producto);
            }
        } catch (err) {
            setIsLoading(false);
        }
    };

    const actualizarCantidadProducto = async (producto: IProductCart, nuevaCantidad: number) => {
        if (!producto || !producto.row_id) {
            return PattyAlert({
                icon: "error",
                title: "Producto",
                message: "Producto no encontrado",
            });
        }

        if (nuevaCantidad < 1) {
            return eliminarProductoModal(producto);
        }

        if (nuevaCantidad > producto.stock) {
            return PattyAlert({
                icon: "error",
                title: "Stock",
                message: "No hay stock suficiente",
            });
        }

        try {
            setIsLoading(true);
            const url = `${window.origin}/api/cart/update`;
            const response = await axios.post(url, {
                rowId: producto.row_id,
                qty: nuevaCantidad,
            });
            if (response.status === 200) {
                actualizarCantidad(producto.row_id, nuevaCantidad);
                setIsLoading(false);
            }
        } catch (err) {
            setIsLoading(false);
            PattyAlert({
                icon: "error",
                title: "Error",
                message: "No se pudo actualizar la cantidad",
            });
        }
    };


    useEffect(() => {
        //if the user click outside the modal close it if isModalOpen is true
        const handleClickOutside = (event: MouseEvent) => {
            const modal = document.querySelector(".modal-cart-block");
            if (modal && !modal.contains(event.target as Node)) {
                // Click is outside of the modal, close it
                if (isModalOpen) {
                    closeModal();
                }
            }
        };
        // Attach the listeners to the document
        document.addEventListener("mousedown", handleClickOutside);

        // Manejar el scroll del body
        if (isModalOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

        // Cleanup the event listeners
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.body.style.overflow = "unset";
        };
    }, [isModalOpen, closeModal]);

    return (
        <>
            {isLoading && <CakeLoader />}
            <div className="modal-cart-block" onClick={() => closeModal()}>
                <div
                    className={`modal-cart-main flex flex-col md:flex-row rounded-3xl shadow-2xl overflow-hidden max-h-[95vh] md:h-[90vh] ${isModalOpen ? "open" : ""}`}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    {/* Panel lateral de productos recomendados - solo en desktop */}
                    <div className="left w-full md:w-1/2 bg-gradient-to-br from-gray-50 to-gray-100 py-4 md:py-8 max-lg:hidden relative flex flex-col">
                        {/* Fondo decorativo */}
                        <div
                            className="absolute inset-0 opacity-[0.02]"
                            style={{
                                backgroundImage: 'url("/images/patrones/fondo%20test.webp")',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat'
                            }}
                        ></div>

                        <div className="relative z-10 flex flex-col h-full">
                            <div className="px-4 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-50 to-gray-100 shadow-sm flex-shrink-0 relative">
                                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                                <h3 className="text-xl font-bold text-[#790546] mb-1">
                                    🎂 Podrían interesarte
                                </h3>
                                <p className="text-gray-600 text-sm">
                                    Productos seleccionados especialmente para ti
                                </p>
                            </div>

                            <div className="list px-4 md:px-8 pt-4 space-y-3 md:space-y-4 flex-1 overflow-y-auto">
                                {relacionados.slice(0, 4).map((product) => (
                                    <div
                                        key={product.id}
                                        className="item bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="bg-img flex-shrink-0">
                                                <img
                                                    src={product.imagen}
                                                    width={300}
                                                    height={300}
                                                    alt={product.nombre}
                                                    className="w-16 h-16 rounded-xl object-cover shadow-md"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0 max-w-[200px]">
                                                <div className="name text-sm font-medium text-gray-800 truncate block mb-1" title={product.nombre}>
                                                    {product.nombre}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="product-price text-base font-semibold text-[#790546]">
                                                        $
                                                        {separadorMiles(
                                                            product.descuento > 0
                                                                ? product.precio -
                                                                (product.descuento -
                                                                    product.precio /
                                                                    100)
                                                                : product.precio
                                                        )}
                                                    </div>
                                                    {product.descuento > 0 && (
                                                        <div className="product-origin-price text-sm text-gray-400">
                                                            <del>${product.precio}</del>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <a
                                                className="bg-[#790546] w-10 h-10 rounded-xl flex items-center justify-center duration-300 cursor-pointer hover:bg-[#9a0a5a] hover:scale-110 transition-all duration-300 shadow-md flex-shrink-0"
                                                href={
                                                    product.producto_es_listable === 0
                                                        ? `/producto/${product.nombreProducto}`
                                                        : `/producto/${product.nombreProducto}/${product.sabor}`
                                                }
                                            >
                                                <Icon.Handbag className="text-white" size={16} />
                                            </a>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Lightbox para imágenes */}
                    <Lightbox
                        open={showProduct}
                        close={() => setShowProduct(false)}
                        slides={[
                            {
                                src: productImage[0]?.image,
                                title: productImage[0]?.title,
                                description: productImage[0]?.description,
                            },
                        ]}
                        render={{
                            buttonNext: () => {
                                return null;
                            },
                            buttonPrev: () => {
                                return null;
                            },
                        }}
                        plugins={[Captions]}
                        captions={{ ref: captionsRef }}
                    />

                    {/* Sección principal del carrito */}
                    <div className="right cart-block w-full lg:w-1/2 bg-white relative overflow-hidden flex flex-col">
                        {/* Fondo decorativo */}
                        <div
                            className="absolute inset-0 opacity-[0.02]"
                            style={{
                                backgroundImage: 'url("/images/patrones/fondo%20test.webp")',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat'
                            }}
                        ></div>

                        <div className="relative z-10 flex flex-col h-full">
                            {/* Header */}
                            <div className="heading px-4 md:px-8 py-4 bg-gradient-to-r from-gray-50 to-gray-100 shadow-sm flex items-center justify-between flex-shrink-0 relative">
                                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                                <div>
                                    <h3 className="text-xl md:text-2xl font-bold text-[#790546] mb-1">
                                        🛒 Mi Carrito
                                    </h3>
                                    <p className="text-gray-600 text-sm">
                                        {productos.length} {productos.length === 1 ? 'producto' : 'productos'} en tu carrito
                                    </p>
                                </div>
                                <div
                                    className="close-btn w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center duration-300 cursor-pointer hover:bg-[#790546] hover:text-white transition-all duration-300"
                                    onClick={() => closeModal()}
                                >
                                    <Icon.X size={20} />
                                </div>
                            </div>

                            {/* Lista de productos */}
                            <div className="list-product px-4 md:px-8 pt-4 overflow-y-auto pb-4">
                                {productos.length === 0 && (
                                    <div className="text-center py-8">
                                        <div className="text-6xl mb-4">🛒</div>
                                        <h4 className="text-xl font-semibold text-gray-800 mb-2">
                                            Tu carrito está vacío
                                        </h4>
                                        <p className="text-gray-600 mb-6">
                                            ¡Agrega algunos productos deliciosos!
                                        </p>
                                        <button
                                            onClick={() => closeModal()}
                                            className="bg-[#790546] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#9a0a5a] transition-all duration-300"
                                        >
                                            🍰 Seguir comprando
                                        </button>
                                    </div>
                                )}

                                {productos.map((product) => (
                                    <div
                                        key={product.row_id}
                                        className="item bg-gray-50 rounded-xl p-3 md:p-4 mb-2 md:mb-4 hover:bg-gray-100 transition-all duration-300"
                                        style={{ minWidth: '100%' }}
                                    >
                                        <div className="flex items-start gap-3 md:gap-4">
                                            <div className="bg-img w-16 h-16 md:w-20 md:h-20 aspect-square flex-shrink-0 rounded-xl overflow-hidden shadow-md">
                                                <img
                                                    src={
                                                        product.es_personalizada
                                                            ? product.imagen_personalizada != null
                                                                ? product.imagen_personalizada
                                                                : product.img
                                                            : product.img
                                                    }
                                                    width={300}
                                                    height={300}
                                                    alt={product.nombre}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-semibold text-gray-800 text-sm mb-1 md:mb-2 line-clamp-2" title={product.nombre}>
                                                    {product.nombre}
                                                </h4>
                                                <div className="space-y-0.5 md:space-y-1 mb-2 md:mb-3">
                                                    {product.esCupon === false && (
                                                        <p className="text-xs text-gray-600">
                                                            Sabor: <span className="text-[#790546] font-semibold">{product.sabor}</span>
                                                        </p>
                                                    )}

                                                    {/* Quantity Controls */}
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-gray-600">Cantidad:</span>
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => actualizarCantidadProducto(product, product.unidades - 1)}
                                                                className="w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-xs font-bold text-gray-600 hover:text-gray-800 transition-colors duration-200"
                                                                disabled={isLoading}
                                                            >
                                                                -
                                                            </button>
                                                            <span className="text-xs font-semibold text-[#790546] min-w-[20px] text-center">
                                                                {product.unidades}
                                                            </span>
                                                            <button
                                                                onClick={() => actualizarCantidadProducto(product, product.unidades + 1)}
                                                                className="w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-xs font-bold text-gray-600 hover:text-gray-800 transition-colors duration-200"
                                                                disabled={isLoading || product.unidades >= product.stock}
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {product.esCupon === false && (
                                                        <p className="text-xs text-gray-600">
                                                            Personas: <span className="text-[#790546] font-semibold">{product.personas}</span>
                                                        </p>
                                                    )}
                                                    {product.esCupon === false && (
                                                        <p className="text-xs text-gray-600">
                                                            Reservado para: <span className="text-[#790546] font-semibold">{product.reserva?.toString()}</span>
                                                        </p>
                                                    )}
                                                    {product.mensaje !== "Mensaje no indicado" && product.esCupon === false && (
                                                        <p className="text-xs text-gray-600">
                                                            Mensaje: <span className="text-[#790546] font-semibold truncate block">{product.mensaje}</span>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Precio y acciones */}
                                            <div className="flex flex-col items-end gap-2">
                                                <div className="text-lg font-bold text-[#790546]">
                                                    ${separadorMiles(
                                                        product.descuento > 0
                                                            ? (product.valor || 0) - ((product.descuento || 0) * (product.valor || 0) / 100)
                                                            : (product.valor || 0)
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => eliminarProductoModal(product)}
                                                    className="text-red-500 hover:text-red-700 text-xs font-semibold hover:underline transition-colors duration-200"
                                                >
                                                    🗑️ Eliminar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Footer del carrito - DENTRO DEL MODAL */}
                            <div className="footer-modal bg-white border-t border-gray-200 flex-shrink-0 shadow-lg">
                                {/* Cálculo de envío */}
                                <div className="px-4 md:px-8 py-2 md:py-3 border-b border-gray-100">
                                    <div
                                        className="flex items-center justify-center gap-3 cursor-pointer hover:bg-gray-50 p-2 md:p-3 rounded-xl transition-colors duration-200"
                                        onClick={() => setActiveTab("shipping")}
                                    >
                                        <Icon.Truck className="text-lg md:text-xl text-[#790546]" />
                                        <span className="text-sm font-medium text-gray-700">
                                            Calcular envío
                                        </span>
                                    </div>
                                </div>

                                {/* Subtotal */}
                                <div className="px-4 md:px-8 py-3 md:py-4 border-b border-gray-100">
                                    <div className="flex items-center justify-between">
                                        <span className="text-base md:text-lg font-semibold text-gray-800">Subtotal</span>
                                        <span className="text-xl md:text-2xl font-bold text-[#790546]">
                                            ${separadorMiles(totalCart)}
                                        </span>
                                    </div>
                                </div>

                                {/* Botones de acción */}
                                <div className="px-4 md:px-8 py-4 md:py-6 space-y-3 md:space-y-4">
                                    <div className="flex gap-2 md:gap-3">
                                        <button
                                            type="button"
                                            className="flex-1 bg-white border-2 border-gray-300 text-gray-700 py-2.5 md:py-3 px-3 md:px-4 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 text-sm md:text-base"
                                            onClick={() => vaciarCarrito()}
                                            disabled={isLoading}
                                        >
                                            🗑️ Vaciar carrito
                                        </button>
                                        <a
                                            href={"/checkout/cart"}
                                            className="flex-1 bg-gradient-to-r from-[#790546] to-[#9a0a5a] text-white py-2.5 md:py-3 px-3 md:px-4 rounded-xl font-bold text-center hover:from-[#9a0a5a] hover:to-[#790546] hover:scale-105 transition-all duration-300 shadow-lg text-sm md:text-base"
                                            onClick={() => closeModal()}
                                        >
                                            🛒 Finalizar compra
                                        </a>
                                    </div>
                                    <button
                                        onClick={() => closeModal()}
                                        className="w-full text-center text-[#790546] hover:underline font-medium py-1.5 md:py-2 transition-colors duration-200 text-sm"
                                    >
                                        🍰 Continuar comprando
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tab de cálculo de envío */}
                    {activeTab === "shipping" && (
                        <>
                            {/* Overlay con blur */}
                            <div className="absolute inset-0 bg-black/20 z-20"></div>
                            {/* Modal */}
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white z-30 p-6 rounded-2xl shadow-2xl border border-gray-200 w-96 max-w-[90vw]">
                                <div className="flex items-center gap-3 mb-4">
                                    <Icon.Truck className="text-lg text-[#790546]" />
                                    <h3 className="text-base font-semibold text-gray-800">
                                        Valor estimado de envío
                                    </h3>
                                </div>

                                <div className="mb-4">
                                    <label
                                        htmlFor="select-country"
                                        className="block text-sm font-medium text-gray-700 mb-2"
                                    >
                                        Comuna
                                    </label>
                                    <div className="relative">
                                        <select
                                            id="select-country"
                                            name="select-country"
                                            className="w-full py-2 pl-3 pr-8 rounded-lg bg-white border border-gray-300 focus:border-[#790546] focus:ring-1 focus:ring-[#790546]/20 text-sm"
                                            defaultValue={""}
                                            onChange={(e) =>
                                                setComunaSeleccionada(
                                                    parseInt(e.target.value)
                                                )
                                            }
                                        >
                                            <option value="" disabled>
                                                Selecciona una comuna
                                            </option>
                                            {comunas.map((comuna) => (
                                                <option
                                                    key={comuna.id}
                                                    value={comuna.id}
                                                >
                                                    {comuna.nombre}
                                                </option>
                                            ))}
                                        </select>
                                        <Icon.CaretDown
                                            size={14}
                                            className="absolute top-1/2 -translate-y-1/2 right-2 text-gray-400"
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        onClick={() => calcularEnvio()}
                                        className="flex-1 bg-[#790546] text-white py-2 px-3 rounded-lg font-medium hover:bg-[#9a0a5a] transition-colors duration-300 text-sm"
                                    >
                                        Calcular envío
                                    </button>
                                    <button
                                        onClick={() => setActiveTab("")}
                                        className="px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200 text-sm"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default CartPrev;